import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import Header from './components/Header';
import BriefingPage from './components/BriefingPage';
import AdminPanel from './components/AdminPanel';
import ProfilePage from './components/ProfilePage';
import AccessDenied from './components/AccessDenied';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

function AppContent() {
  const { isAuthenticated, isAdmin, isMember, loading } = useAuth();
  const approved = isAuthenticated && (isAdmin || isMember);

  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('goa-theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('goa-theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  // Show loading while auth is initializing
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'linear-gradient(135deg, #0e7490 0%, #065f46 50%, #1e3a5f 100%)' }}>
        <div style={{ background: 'rgba(255,255,255,0.95)', borderRadius: '16px', padding: '2rem', textAlign: 'center', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
          <div className="spinner" style={{ width: '40px', height: '40px', margin: '0 auto 1rem', borderRadius: '50%', border: '4px solid #f3f3f3', borderTop: '4px solid #0e7490', animation: 'spin 1s linear infinite' }}></div>
          <h2 style={{ color: '#0e7490' }}>Loading...</h2>
          <p style={{ color: '#666' }}>Initializing authentication</p>
        </div>
      </div>
    );
  }

  // If authenticated but not approved, show access denied
  if (isAuthenticated && !approved) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<AccessDenied />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <AppRoutes isAuthenticated={isAuthenticated} approved={approved} isAdmin={isAdmin} toggleTheme={toggleTheme} />
    </Router>
  );
}

function AppRoutes({ isAuthenticated, approved, isAdmin, toggleTheme }) {
  const location = useLocation();
  // Extract current page from URL for nav highlighting
  const pageMatch = location.pathname.match(/\/briefing\/(\d+)/);
  const currentPage = pageMatch ? pageMatch[1] : null;

  return (
    <>
      {isAuthenticated && <Header currentPage={currentPage} onThemeToggle={toggleTheme} />}
      
      <Routes>
        <Route path="/" element={isAuthenticated && approved ? <Navigate to="/briefing/01" /> : <LoginPage />} />
        <Route
          path="/briefing/:pageNum"
          element={
            <ProtectedRoute>
              <BriefingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route path="*" element={<Navigate to={isAuthenticated && approved ? '/briefing/01' : '/'} />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
